// src/pages/IntegratedSessions.js - PREMIUM + PROFESSIONAL (same content)
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./IntegratedSessions.css";
import Footer from "../components/Footer";
import AnimatedHeroBackground from "../components/AnimatedHeroBackground";

const IntegratedSessions = () => {
  const [activeTrack, setActiveTrack] = useState("aiml");

  // ===== DATA (same content you provided) =====
  const programHighlights = [
    { icon: "⏱️", title: "300-600+ Hours", description: "Comprehensive hands-on training over 1-2 years" },
    { icon: "🎓", title: "Joint Certification", description: "From Crafting Brain & iNikola Technologies" },
    { icon: "🤝", title: "Industry Mentorship", description: "Learn from professionals working in leading tech companies" },
    { icon: "🤖", title: "EVALSEA Platform", description: "AI-powered learning & performance tracking system" }
  ];

  const studentGrowthJourney = [
    {
      phase: "Foundation Building",
      level: "Beginner",
      duration: "0-3 Months",
      icon: "🌱",
      description: "Starting from scratch - building strong fundamentals",
      skills: [
        "Programming fundamentals (Python/R)",
        "Data structures & algorithms",
        "Mathematics for AI/Data Science",
        "Version control & collaboration tools"
      ],
      outcomes: "Students gain confidence in coding and understand core concepts"
    },
    {
      phase: "Skill Development",
      level: "Intermediate",
      duration: "3-8 Months",
      icon: "📈",
      description: "Developing industry-relevant technical skills",
      skills: [
        "Machine Learning algorithms & frameworks",
        "Data analysis & visualization",
        "Cloud computing fundamentals",
        "Real-world project implementation"
      ],
      outcomes: "Students can build projects independently and solve practical problems"
    },
    {
      phase: "Specialization",
      level: "Advanced",
      duration: "8-12 Months",
      icon: "🎯",
      description: "Deep-diving into specialized domains",
      skills: [
        "Advanced AI/ML techniques",
        "Big Data & distributed systems",
        "Production-grade deployment",
        "Industry best practices & tools"
      ],
      outcomes: "Students develop expertise in chosen specialization with portfolio projects"
    },
    {
      phase: "Industry Readiness",
      level: "Professional",
      duration: "12+ Months",
      icon: "🚀",
      description: "Becoming job-ready professionals",
      skills: [
        "Live project experience with mentors",
        "Interview preparation & mock tests",
        "Soft skills & communication training",
        "Portfolio optimization for placements"
      ],
      outcomes: "Students are confident, job-ready professionals with proven project experience"
    }
  ];

  const curriculumTracks = useMemo(
    () => ({
      aiml: {
        title: "AI & Machine Learning Track",
        duration: "300-600+ Hours",
        description: "Transform from basics to building intelligent systems",
        modules: [
          {
            name: "Foundation Phase",
            topics: [
              "Python Programming from Scratch",
              "Data Structures & Algorithms",
              "Statistics & Linear Algebra",
              "Introduction to Machine Learning"
            ],
            duration: "8-10 weeks",
            studentLevel: "Start with zero knowledge"
          },
          {
            name: "Core Machine Learning",
            topics: [
              "Supervised & Unsupervised Learning",
              "Neural Networks & Deep Learning",
              "Model Training & Evaluation",
              "Real-world ML Projects"
            ],
            duration: "10-12 weeks",
            studentLevel: "Build ML models independently"
          },
          {
            name: "Advanced AI",
            topics: [
              "Natural Language Processing",
              "Computer Vision Applications",
              "Reinforcement Learning",
              "Generative AI & LLMs"
            ],
            duration: "8-10 weeks",
            studentLevel: "Develop advanced AI solutions"
          },
          {
            name: "Industry Projects",
            topics: [
              "Live project with industry mentors",
              "MLOps & Production deployment",
              "Portfolio building",
              "Interview preparation"
            ],
            duration: "6-8 weeks",
            studentLevel: "Industry-ready professional"
          }
        ]
      },
      datascience: {
        title: "Data Science & Analytics Track",
        duration: "300-600+ Hours",
        description: "Journey from data basics to becoming a data expert",
        modules: [
          {
            name: "Foundation Phase",
            topics: [
              "Python/R Programming Basics",
              "SQL & Database Management",
              "Statistics Fundamentals",
              "Data Visualization Basics"
            ],
            duration: "8-10 weeks",
            studentLevel: "Start with zero technical background"
          },
          {
            name: "Data Engineering",
            topics: [
              "Data Cleaning & Preprocessing",
              "ETL Pipelines & Workflows",
              "Big Data Technologies (Hadoop/Spark)",
              "Cloud Data Platforms (AWS/Azure)"
            ],
            duration: "10-12 weeks",
            studentLevel: "Handle large-scale data independently"
          },
          {
            name: "Analytics & Insights",
            topics: [
              "Exploratory Data Analysis",
              "Predictive Analytics & ML",
              "Business Intelligence Tools",
              "A/B Testing & Experimentation"
            ],
            duration: "8-10 weeks",
            studentLevel: "Derive insights from complex data"
          },
          {
            name: "Industry Projects",
            topics: [
              "End-to-end data solutions",
              "Live business problem solving",
              "Dashboard & report creation",
              "Interview preparation & mock tests"
            ],
            duration: "6-8 weeks",
            studentLevel: "Industry-ready data professional"
          }
        ]
      },
      emerging: {
        title: "Emerging Technologies Track",
        duration: "300-600+ Hours",
        description: "Master cutting-edge technologies for future careers",
        modules: [
          {
            name: "Foundation Phase",
            topics: [
              "Programming Fundamentals",
              "Web Development Basics",
              "Cloud Computing Introduction",
              "Technology Landscape Overview"
            ],
            duration: "8-10 weeks",
            studentLevel: "Start from fundamentals"
          },
          {
            name: "Cloud & DevOps",
            topics: [
              "AWS/Azure/GCP Platforms",
              "Containerization (Docker/Kubernetes)",
              "CI/CD Pipelines",
              "Infrastructure as Code"
            ],
            duration: "10-12 weeks",
            studentLevel: "Deploy cloud applications"
          },
          {
            name: "Specialized Technologies",
            topics: [
              "Blockchain & Smart Contracts",
              "IoT & Edge Computing",
              "Cybersecurity Essentials",
              "AR/VR Development"
            ],
            duration: "8-10 weeks",
            studentLevel: "Build innovative solutions"
          },
          {
            name: "Full-Stack Integration",
            topics: [
              "Complete application development",
              "Live project with mentors",
              "Technology integration",
              "Portfolio & interview prep"
            ],
            duration: "6-8 weeks",
            studentLevel: "Full-stack technology professional"
          }
        ]
      }
    }),
    []
  );

  const evalseaFeatures = [
    {
      icon: "📊",
      title: "Real-time Progress Tracking",
      description: "Track your learning journey from day one to industry readiness",
      benefits: ["Live performance dashboards", "Skill development visualization", "Weekly progress reports", "Personalized learning insights"]
    },
    {
      icon: "🎯",
      title: "AI-Powered Assessment",
      description: "Automated evaluation ensures continuous learning and improvement",
      benefits: ["Instant assignment feedback", "Auto-graded coding exercises", "Plagiarism detection", "Code quality analysis"]
    },
    {
      icon: "📈",
      title: "Growth Analytics",
      description: "Visualize your transformation from beginner to professional",
      benefits: ["Skill progression tracking", "Competency mapping", "Learning milestones", "Industry readiness score"]
    },
    {
      icon: "👥",
      title: "Transparent Communication",
      description: "Keep everyone informed about student progress",
      benefits: ["Parent portal access", "Faculty performance insights", "Regular progress updates", "Direct mentor communication"]
    }
  ];

  const internshipPathway = [
    { step: "01", title: "Training Completion", description: "Successfully complete the comprehensive training program with hands-on projects", icon: "🎓", outcome: "Strong technical foundation & project portfolio" },
    { step: "02", title: "Live Project Experience", description: "Work on real industry projects under guidance of experienced mentors", icon: "💼", outcome: "Practical experience with production-level work" },
    { step: "03", title: "Interview Preparation", description: "Comprehensive mock interviews, resume building & soft skills training", icon: "💬", outcome: "Confidence to face technical & HR interviews" },
    { step: "04", title: "Internship Opportunities", description: "Top performers get access to paid internships with our partner companies", icon: "🚀", outcome: "Real corporate exposure with stipend for deserving students" },
    { step: "05", title: "Career Launch", description: "Ongoing guidance for full-time opportunities through our industry network", icon: "🎯", outcome: "Smooth transition from student to working professional" }
  ];

  const programFeatures = [
    { icon: "👨‍🏫", title: "Industry Expert Mentors", description: "Learn from professionals currently working in leading tech companies", points: ["Real-world experience sharing", "Live project guidance", "Industry best practices", "Career counseling"] },
    { icon: "🏢", title: "Industry Visits & Guest Sessions", description: "Regular interactions with tech professionals and company visits", points: ["Guest lectures by industry experts", "Company culture exposure", "Networking opportunities", "Technology trend insights"] },
    { icon: "📝", title: "Mock Tests & Assessments", description: "Regular evaluations to ensure continuous improvement", points: ["Weekly coding assessments", "Mock technical interviews", "HR interview simulations", "Aptitude & reasoning tests"] },
    { icon: "💰", title: "Paid Internship Opportunities", description: "Top-performing students get access to paid internships", points: ["Merit-based selection", "Work on live projects", "Corporate work environment", "Stipend for top performers"] },
    { icon: "🤝", title: "Industry Partner Network", description: "Connected with leading technology companies for opportunities", points: ["Access to multiple companies", "Various domain opportunities", "Continuous job referrals", "Long-term career support"] },
    { icon: "☁️", title: "Free Cloud & Tool Access", description: "Hands-on practice with professional tools and platforms", points: ["AWS/Azure cloud credits", "GenAI tools access", "Premium software licenses", "Professional development tools"] }
  ];

  const trainerProfiles = [
    {
      name: "Mr. Bilal Ahmad",
      role: "Technical Trainer",
      rating: "4.55/5",
      image: "👨‍💼",
      expertise: "Gen AI Data Scientist | AI & ML Expert | Mentor",
      experience: "Currently working as Data Scientist at iNikola Pvt Ltd with 2+ years of experience",
      highlights: ["Trained 300+ students in leading institutions", "Expert in LLMs, Deep Learning, and Gen AI", "Hands-on approach in AI solution implementation", "Transforms theory into practical applications"]
    },
    {
      name: "Mr. Vushagoni Rohith",
      role: "Technical Trainer",
      rating: "4.50/5",
      image: "👨‍💻",
      expertise: "Cloud Architect | Gen AI Developer | ML & API Expert",
      experience: "1.5+ years designing scalable AI-Cloud systems using AWS and Azure",
      highlights: ["Trained hundreds of students nationwide", "Expert in cloud architecture & deployment", "Simplifies complex ML & DL concepts", "Engaging delivery with practical examples"]
    },
    {
      name: "Mr. Maharaj Mukherjee",
      role: "Data Scientist",
      rating: "4.52/5",
      image: "👨‍🔬",
      expertise: "Gen AI Data Scientist | Python & AWS Specialist",
      experience: "Focused on applying Data Science and AI to solve real business problems",
      highlights: ["Trained large student batches across colleges", "2.5 years of experience at iNikola", "Develops AI-driven automation systems", "Mentors on end-to-end ML pipeline implementation"]
    }
  ];

  const studentTestimonials = [
    { name: "Priya Sharma", college: "Sphoorthy Engineering College", course: "AI & ML Track", image: "👩‍🎓", journey: "Started with zero coding knowledge, now confident in building ML models", quote: "The step-by-step approach helped me understand complex AI concepts. The project work gave me real confidence.", transformation: "From beginner to building AI applications independently" },
    { name: "Rahul Verma", college: "IARE Engineering College", course: "Data Science Track", image: "👨‍🎓", journey: "Transformed from theory student to handling real-world data projects", quote: "EVALSEA platform made learning interactive. Mock interviews prepared me for actual company interviews.", transformation: "From classroom learning to corporate-ready professional" },
    { name: "Sneha Reddy", college: "VNR Vignana Jyothi", course: "AI & ML Track", image: "👩‍🎓", journey: "Learned everything from scratch to building production-ready AI systems", quote: "Industry mentors shared real experiences. The internship opportunity gave me actual work exposure.", transformation: "From student to contributing in live industry projects" }
  ];

  const collegeTestimonials = [
    { name: "Dr. Ramesh", position: "Principal, Sphoorthy Engineering College", quote: "After partnering with Crafting Brain, our students' efficiency and confidence improved significantly. The practical approach exceeded our expectations.", rating: 5 },
    { name: "Mr. Prasad", position: "TPO, IARE Engineering College", quote: "The sessions were impactful and industry-aligned. We see remarkable improvement in our students' technical abilities and interview performance.", rating: 5 },
    { name: "Dr. Lalitha", position: "HOD CSE, VNR Vignana Jyothi Engineering College", quote: "The hands-on learning approach and industry mentorship made a significant difference. We're excited for long-term collaboration.", rating: 5 }
  ];

  const active = curriculumTracks[activeTrack];

  return (
    <AnimatedHeroBackground>
      <div className="integrated-sessions-page premium">
        {/* ===== HERO ===== */}
        <header className="hero-wrap">
          <div className="container">
            <div className="hero-top">
              <div className="breadcrumb">
                <Link to="/for-colleges">For Colleges</Link>
                <span className="sep">/</span>
                <span>Integrated Sessions</span>
              </div>

              <div className="hero-badges">
                <span className="badge soft">In Collaboration with iNikola</span>
                <span className="badge soft">300–600+ Hours</span>
                <span className="badge soft">Industry Mentorship</span>
              </div>
            </div>

            <div className="hero-grid">
              <div className="hero-copy">
                <h1 className="hero-title">Integrated Training Sessions</h1>
                <p className="hero-subtitle">
                  Transforming students from beginners to industry-ready professionals through comprehensive 300–600+ hours
                  training with real-world projects, mentorship, and a measurable growth journey.
                </p>

                <div className="hero-cta">
                  <Link to="/contact" className="btn btn-primary">
                    Schedule Campus Demo <span aria-hidden>📅</span>
                  </Link>
                  <a href="/Brochure_A4_FullBleed_v2.pdf" download className="btn btn-secondary">
                    Download Brochure <span aria-hidden>📥</span>
                  </a>
                </div>

                <div className="hero-metrics">
                  {programHighlights.map((h, i) => (
                    <div key={i} className="metric">
                      <div className="metric-ic">{h.icon}</div>
                      <div className="metric-txt">
                        <div className="metric-title">{h.title}</div>
                        <div className="metric-sub">{h.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* right decorative panel (no images) */}
              <div className="hero-panel" aria-hidden>
                <div className="panel-card">
                  <div className="panel-title">Student Growth Journey</div>
                  <div className="panel-sub">A structured, trackable transformation roadmap.</div>
                  <div className="panel-steps">
                    {studentGrowthJourney.map((p, idx) => (
                      <div key={idx} className="panel-step">
                        <div className="ps-ic">{p.icon}</div>
                        <div className="ps-t">
                          <div className="ps-name">{p.phase}</div>
                          <div className="ps-meta">{p.duration} • {p.level}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="panel-glow" />
              </div>
            </div>
          </div>
        </header>

        {/* ===== SECTION: JOURNEY ===== */}
        <section className="section">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">Student Growth Journey</h2>
              <p className="section-subtitle">A clear transformation path from zero to industry professional.</p>
            </div>

            <div className="timeline">
              {studentGrowthJourney.map((phase, idx) => (
                <article key={idx} className="timeline-item">
                  <div className="timeline-node">
                    <span className="node-icon">{phase.icon}</span>
                  </div>

                  <div className="timeline-card">
                    <div className="timeline-top">
                      <h3 className="timeline-title">{phase.phase}</h3>
                      <div className="chips">
                        <span className="chip chip-solid">{phase.level}</span>
                        <span className="chip chip-soft">{phase.duration}</span>
                      </div>
                    </div>
                    <p className="timeline-desc">{phase.description}</p>

                    <div className="two-col">
                      <div className="box">
                        <div className="box-title">Key Skills Developed</div>
                        <ul className="list">
                          {phase.skills.map((s, i) => (
                            <li key={i}><span className="tick">✓</span>{s}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="box highlight">
                        <div className="box-title">Outcome</div>
                        <p className="box-text">{phase.outcomes}</p>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECTION: TRACKS ===== */}
        <section className="section alt">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">Choose Your Learning Path</h2>
              <p className="section-subtitle">Specialized tracks designed to take students from beginner to job-ready.</p>
            </div>

            <div className="tabs">
              <button className={`tab ${activeTrack === "aiml" ? "active" : ""}`} onClick={() => setActiveTrack("aiml")}>
                🤖 AI & Machine Learning
              </button>
              <button className={`tab ${activeTrack === "datascience" ? "active" : ""}`} onClick={() => setActiveTrack("datascience")}>
                📊 Data Science & Analytics
              </button>
              <button className={`tab ${activeTrack === "emerging" ? "active" : ""}`} onClick={() => setActiveTrack("emerging")}>
                ☁️ Emerging Technologies
              </button>
            </div>

            <div className="track-shell">
              <div className="track-head">
                <div>
                  <h3 className="track-title">{active.title}</h3>
                  <p className="track-desc">{active.description}</p>
                </div>
                <div className="track-pill">⏱️ {active.duration}</div>
              </div>

              <div className="grid cards-4">
                {active.modules.map((m, i) => (
                  <div key={i} className="card">
                    <div className="card-top">
                      <span className="kicker">Phase {i + 1}</span>
                      <span className="muted">{m.duration}</span>
                    </div>
                    <div className="card-title">{m.name}</div>
                    <div className="badge solid">{m.studentLevel}</div>
                    <ul className="list tight">
                      {m.topics.map((t, idx) => (
                        <li key={idx}><span className="tick">✓</span>{t}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECTION: FEATURES ===== */}
        <section className="section">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">What Makes Our Program Unique</h2>
              <p className="section-subtitle">A complete support system for measurable student outcomes.</p>
            </div>

            <div className="grid cards-3">
              {programFeatures.map((f, i) => (
                <div key={i} className="card">
                  <div className="icon-bubble">{f.icon}</div>
                  <div className="card-title">{f.title}</div>
                  <p className="card-text">{f.description}</p>
                  <ul className="list">
                    {f.points.map((p, idx) => (
                      <li key={idx}><span className="tick">✓</span>{p}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECTION: EVALSEA ===== */}
        <section className="section alt">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">EVALSEA Platform</h2>
              <p className="section-subtitle">AI-powered learning management with real-time growth tracking.</p>
            </div>

            <div className="grid cards-2">
              {evalseaFeatures.map((e, i) => (
                <div key={i} className="card">
                  <div className="icon-bubble">{e.icon}</div>
                  <div className="card-title">{e.title}</div>
                  <p className="card-text">{e.description}</p>
                  <ul className="list">
                    {e.benefits.map((b, idx) => (
                      <li key={idx}><span className="tick">✓</span>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECTION: PATHWAY ===== */}
        <section className="section">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">From Training to Career Launch</h2>
              <p className="section-subtitle">A 5-step pathway guiding students from learning to professional growth.</p>
            </div>

            <div className="grid cards-5">
              {internshipPathway.map((s, i) => (
                <div key={i} className="card center">
                  <div className="step-num">{s.step}</div>
                  <div className="icon-bubble">{s.icon}</div>
                  <div className="card-title">{s.title}</div>
                  <p className="card-text">{s.description}</p>
                  <div className="note">
                    <strong>Outcome:</strong> {s.outcome}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECTION: TRAINERS ===== */}
        <section className="section alt">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">Learn from Industry Experts</h2>
              <p className="section-subtitle">Real professionals sharing real-world implementation experience.</p>
            </div>

            <div className="grid cards-3">
              {trainerProfiles.map((t, i) => (
                <div key={i} className="card">
                  <div className="trainer-top">
                    <div className="avatar">{t.image}</div>
                    <div className="trainer-meta">
                      <div className="trainer-name">{t.name}</div>
                      <div className="trainer-role">{t.role}</div>
                    </div>
                  </div>

                  <div className="trainer-rating">⭐ {t.rating} | Multiple College Trainings</div>
                  <div className="badge solid">{t.expertise}</div>
                  <p className="card-text">{t.experience}</p>

                  <ul className="list">
                    {t.highlights.map((h, idx) => (
                      <li key={idx}><span className="tick">✓</span>{h}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECTION: STUDENT STORIES ===== */}
        <section className="section">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">Student Transformation Stories</h2>
              <p className="section-subtitle">Real outcomes through structured learning and mentorship.</p>
            </div>

            <div className="grid cards-3">
              {studentTestimonials.map((s, i) => (
                <div key={i} className="card">
                  <div className="testimonial-top">
                    <div className="avatar">{s.image}</div>
                    <div>
                      <div className="trainer-name">{s.name}</div>
                      <div className="muted">{s.college} • {s.course}</div>
                    </div>
                  </div>
                  <div className="badge soft">{s.journey}</div>
                  <p className="quote">“{s.quote}”</p>
                  <div className="note">
                    <strong>Transformation:</strong> {s.transformation}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECTION: COLLEGE TESTIMONIALS ===== */}
        <section className="section alt">
          <div className="container">
            <div className="section-head">
              <h2 className="section-title">What College Leaders Say</h2>
              <p className="section-subtitle">Trusted feedback from leadership across partner colleges.</p>
            </div>

            <div className="grid cards-3">
              {collegeTestimonials.map((c, i) => (
                <div key={i} className="card">
                  <div className="stars">{"⭐".repeat(c.rating)}</div>
                  <p className="quote">“{c.quote}”</p>
                  <div className="divider" />
                  <div className="trainer-name">{c.name}</div>
                  <div className="muted">{c.position}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="cta">
          <div className="container">
            <div className="cta-card">
              <div>
                <h2 className="cta-title">Ready to Transform Your Students?</h2>
                <p className="cta-subtitle">
                  Join 10+ leading colleges in providing industry-ready training to students through structured tracks,
                  projects, and placement mentorship.
                </p>
              </div>
              <div className="cta-actions">
                <Link to="/contact" className="btn btn-primary">
                  Schedule Campus Demo <span aria-hidden>📅</span>
                </Link>
                <a href="/Brochure_A4_FullBleed_v2.pdf" download className="btn btn-secondary">
                  Download Detailed Brochure <span aria-hidden>📥</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </AnimatedHeroBackground>
  );
};

export default IntegratedSessions;